/**
 * Intelligent Resource Loader
 * Priority-based resource loading with network awareness
 * @version 1.0.0
 */

class IntelligentResourceLoader {
  constructor() {
    this.queue = {
      critical: [],
      high: [],
      medium: [],
      low: []
    };
    this.loading = new Set();
    this.loaded = new Set();
    this.maxConcurrent = this.getMaxConcurrent();
    this.networkSpeed = this.detectNetworkSpeed();
    this.init();
  }

  init() {
    // Monitor network changes
    if ('connection' in navigator) {
      navigator.connection.addEventListener('change', () => {
        this.networkSpeed = this.detectNetworkSpeed();
        this.adjustStrategy();
      });
    }

    // Start processing queue
    this.processQueue();
  }

  /**
   * Detect network speed category
   */
  detectNetworkSpeed() {
    if (!('connection' in navigator)) {
      return 'fast'; // Default to fast if not supported
    }

    const conn = navigator.connection;
    const effectiveType = conn.effectiveType;

    // Map effective types to speed categories
    const speedMap = {
      'slow-2g': 'slow',
      '2g': 'slow',
      '3g': 'medium',
      '4g': 'fast'
    };

    return speedMap[effectiveType] || 'fast';
  }

  /**
   * Get max concurrent requests based on network
   */
  getMaxConcurrent() {
    const speed = this.detectNetworkSpeed();
    const concurrentMap = {
      slow: 2,
      medium: 4,
      fast: 6
    };
    return concurrentMap[speed] || 4;
  }

  /**
   * Adjust loading strategy based on network change
   */
  adjustStrategy() {
    this.maxConcurrent = this.getMaxConcurrent();
    console.log(`📡 Network changed to ${this.networkSpeed}, adjusting concurrent loads to ${this.maxConcurrent}`);
    this.processQueue();
  }

  /**
   * Add resource to queue
   * @param {string} url - Resource URL
   * @param {string} priority - Priority level: critical, high, medium, low
   * @param {string} type - Resource type: script, style, image
   * @param {Object} options - Additional options
   */
  load(url, priority = 'medium', type = 'script', options = {}) {
    // Skip if already loaded or loading
    if (this.loaded.has(url) || this.loading.has(url)) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const resource = {
        url,
        type,
        priority,
        options,
        resolve,
        reject,
        timestamp: Date.now()
      };

      // Add to appropriate queue
      if (this.queue[priority]) {
        this.queue[priority].push(resource);
      } else {
        this.queue.medium.push(resource);
      }

      // Start processing
      this.processQueue();
    });
  }

  /**
   * Process the resource queue
   */
  processQueue() {
    // Don't exceed max concurrent
    if (this.loading.size >= this.maxConcurrent) {
      return;
    }

    // Process in priority order
    const priorities = ['critical', 'high', 'medium', 'low'];

    for (const priority of priorities) {
      while (this.queue[priority].length > 0 && this.loading.size < this.maxConcurrent) {
        const resource = this.queue[priority].shift();
        this.loadResource(resource);
      }
    }
  }

  /**
   * Load a single resource
   */
  async loadResource(resource) {
    const { url, type, options, resolve, reject } = resource;

    this.loading.add(url);

    try {
      let element;

      switch (type) {
        case 'script':
          element = await this.loadScript(url, options);
          break;
        case 'style':
          element = await this.loadStyle(url, options);
          break;
        case 'image':
          element = await this.loadImage(url, options);
          break;
        case 'fetch':
          element = await this.loadFetch(url, options);
          break;
        default:
          throw new Error(`Unknown resource type: ${type}`);
      }

      this.loading.delete(url);
      this.loaded.add(url);
      resolve(element);

      // Process next item in queue
      this.processQueue();

    } catch (error) {
      this.loading.delete(url);
      console.error(`Failed to load ${type}: ${url}`, error);
      reject(error);

      // Continue with next item
      this.processQueue();
    }
  }

  /**
   * Load JavaScript file
   */
  loadScript(url, options = {}) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;

      if (options.defer) script.defer = true;
      if (options.module) script.type = 'module';
      if (options.crossOrigin) script.crossOrigin = options.crossOrigin;

      script.onload = () => resolve(script);
      script.onerror = () => reject(new Error(`Script load failed: ${url}`));

      document.head.appendChild(script);
    });
  }

  /**
   * Load CSS file
   */
  loadStyle(url, options = {}) {
    return new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;

      if (options.media) link.media = options.media;
      if (options.crossOrigin) link.crossOrigin = options.crossOrigin;

      link.onload = () => resolve(link);
      link.onerror = () => reject(new Error(`Style load failed: ${url}`));

      document.head.appendChild(link);
    });
  }

  /**
   * Load image
   */
  loadImage(url, options = {}) {
    return new Promise((resolve, reject) => {
      const img = new Image();

      if (options.crossOrigin) img.crossOrigin = options.crossOrigin;
      if (options.sizes) img.sizes = options.sizes;
      if (options.srcset) img.srcset = options.srcset;

      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Image load failed: ${url}`));

      img.src = url;
    });
  }

  /**
   * Fetch resource via fetch API
   */
  async loadFetch(url, options = {}) {
    const response = await fetch(url, options);
    if (!response.ok) {
      throw new Error(`Fetch failed: ${url} (${response.status})`);
    }
    return response;
  }

  /**
   * Preconnect to domain
   */
  preconnect(url) {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = url;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  /**
   * DNS prefetch
   */
  dnsPrefetch(url) {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = url;
    document.head.appendChild(link);
  }

  /**
   * Get queue status
   */
  getStatus() {
    return {
      network: this.networkSpeed,
      maxConcurrent: this.maxConcurrent,
      loading: this.loading.size,
      loaded: this.loaded.size,
      queued: {
        critical: this.queue.critical.length,
        high: this.queue.high.length,
        medium: this.queue.medium.length,
        low: this.queue.low.length
      }
    };
  }

  /**
   * Clear all queues (emergency brake)
   */
  clearQueues() {
    Object.keys(this.queue).forEach(key => {
      this.queue[key] = [];
    });
  }
}

// Create global instance
window.resourceLoader = new IntelligentResourceLoader();

// Log initial status
console.log('🚀 Intelligent Resource Loader initialized:', window.resourceLoader.getStatus());

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = IntelligentResourceLoader;
}
